import React from 'react';
import PropTypes from 'prop-types';
import useSandbox from '../../hooks/useSandbox';
import useProjectServices from '../../hooks/useProjectServices';
import { isRunning } from '../../services/SandboxService';
import { servicePath } from '../../utils/projectOverview';
import { ownerOfSession, servesItsOwn } from './projectRun';
import { PlanChangeButton } from './PlanRequest';
import './sandbox.css';

/**
 * "● 2 of 2 running" for a project in the top bar (sandbox-first); nothing until the sandbox answers. Near a limit of
 * the plan: "Near a limit" (the limits in its tooltip) and "Ask for another plan" beside it (PlanChangeButton; nothing
 * where there is no other plan).
 */
/**
 * With `serviceKeys` (the project's services as its configuration lists them) the count is of those: a process started
 * in a subfolder of a service (the `frontend/` of a monorepo, run by the agent on its own) is part of that service, not a
 * service of its own, so "Running 1/2" never comes from a folder the project does not list as a service. A service is
 * running when its own process or one in its subfolders runs, or one of its pinned files' sessions
 * (`services/app--compose-…`) runs: a service with no stack of its own (or a failed build) whose compose file runs is
 * not an error (projectRun foldPinned).
 */
export default function ProjectRunStatus({ projectId, serviceCount = null, serviceKeys = null }) {
  // Near-limit warnings are the caller's own sandbox's; the services are the project's, from the pod it runs in (its
  // owner's, per person), so a project shared with the caller shows its real state.
  const { sandbox } = useSandbox({ intervalMs: 10000 });
  const project = useProjectServices(projectId, { intervalMs: 10000 });
  if (!project.loaded || project.unavailable) { return null; }
  const sessions = project.services;
  const paths = serviceKeys && serviceKeys.length > 0 ? serviceKeys.map(servicePath) : null;
  // One entry per listed service: the worst state among its own process and the ones under its folder.
  const own = paths
    ? paths.map((path) => {
      const mine = sessions.filter((item) => item.serviceName === path || String(item.serviceName || '').startsWith(`${path}/`));
      const stack = sessions.find((item) => ownerOfSession(item.serviceName) === path && isRunning(item) && item.buildStatus !== 'building');
      if (stack && !mine.some((item) => servesItsOwn(item))) { return stack; }
      return mine.find((item) => item.buildStatus === 'building')
        || mine.find((item) => item.crashed || item.buildStatus === 'failed')
        || mine.find((item) => isRunning(item))
        || mine[0]
        || { serviceName: path };
    })
    : sessions;
  // The same store and the same reading as the Preview tab (hooks/usePreviewWatchdog.js): a service still building is
  // "Starting" here too, not already "Running" while the Preview says "Rendering…".
  const up = own.filter((service) => isRunning(service) && service.buildStatus !== 'building').length;
  const starting = own.filter((service) => service.buildStatus === 'building').length;
  const total = paths ? paths.length : Math.max(serviceCount ?? own.length, own.length);
  const crashed = own.some((service) => service.crashed || service.buildStatus === 'failed');
  const warnings = sandbox?.warnings || [];
  if (!total && !warnings.length) { return null; }
  const active = up + starting;
  const tone = crashed ? 'sbx-pill-crashed' : active ? 'sbx-pill-running' : '';
  const label = starting
    ? `${up} of ${total} services running, ${starting} starting`
    : up ? `${up} of ${total} services running` : crashed ? 'A service stopped with an error' : 'Stopped';
  const text = starting ? `Starting ${up}/${total}` : up ? `Running ${up}/${total}` : crashed ? 'Error' : 'Stopped';
  return (
    <>
      {total > 0 && (
        <span
          aria-label={label}
          className={`sbx-pill sbx-pill-fixed ${tone}`}
          title={active ? `${label} in this project's sandbox` : crashed ? 'A service stopped with an error: see Logs' : 'Services of this project running in its sandbox: none'}
        >
          {active ? <span aria-hidden="true" className="sbx-dot" /> : null}
          {/* Short, so the top bar never truncates it (the full sentence is the tooltip and the label). */}
          <span aria-hidden="true">{text}</span>
        </span>
      )}
      {warnings.length > 0 && (
        <span className="sbx-pill sbx-pill-warn" role="status" title={warnings.map((warning) => warning.message).join('\n')}>
          Near a limit
        </span>
      )}
      {warnings.length > 0 && <PlanChangeButton className="sbx-near-limit-plan" />}
    </>
  );
}

ProjectRunStatus.propTypes = {
  projectId: PropTypes.string.isRequired,
  serviceCount: PropTypes.number,
  serviceKeys: PropTypes.arrayOf(PropTypes.string),
};
